/**
 * SDK の中核: HTTP・認証（自動ゲストログイン / リフレッシュ）・保存領域・イベント。
 * 各機能モジュール（saves / stats / rooms / store …）はこの KumoCore だけに依存する。
 *
 * 設計:
 *   - 呼び出し側は認証を意識しない。アクセストークンが切れたら 1 回だけ自動リフレッシュして再試行
 *   - リフレッシュトークンは localStorage（Web ゲームに他の永続領域がない）。キーは publishable key ごとに分ける
 *     （同じドメインで複数のゲーム / 環境を動かしても混ざらない）
 *   - 同時に 10 個の API が 401 になってもリフレッシュは 1 回（Promise を共有）
 *   - **複数タブでも**リフレッシュは同時に 1 本（Web Locks）。下の「複数タブ」参照
 *
 * 複数タブ（2026-09-26 Sky Duel で発見）:
 *   同じゲームの 2 タブは localStorage のリフレッシュトークンを共有する。タブ A が回転した直後にタブ B が古いトークンを
 *   出すと、サーバーの再利用検知が系列ごと失効させ、両方がサインアウトされていた（ゲストはアカウントを失う）。
 *   1. Web Locks（navigator.locks.request('kumo-refresh:<鍵の先頭>')）でタブ間のリフレッシュを直列化する
 *   2. ロックを取ったら保存領域を**読み直す**。自分が持っていたのと違うリフレッシュトークン = 別のタブが回転済み。
 *      そのタブが保存したアクセストークンがまだ有効なら**ネットワークに出ずに**それを使う（無ければ新しい方で回転）
 *   3. Web Locks が無い環境（古いブラウザ・一部の WebView・Node）: サーバーが直前に使われたトークンに
 *      409 refresh_race を返す（系列は失効しない）ので、保存領域が他のタブに書き換えられるのを少し待って読み直し、
 *      1 回だけ再試行する
 *   そのためアクセストークン（15 分）も保存領域に置く（`kumo:<鍵>:access`）。より強いリフレッシュトークン（90 日）が
 *   既に同じ場所にあるので、露出の範囲は広がらない
 */

import { capacitorBridge, nativeDefaultStorage, type KumoPlatform, type NativeBridge } from './native.js';
import { CachedStorage, type KumoStorage, type SyncStorage } from './storage.js';

export interface KumoInitOptions {
  /** publishable key（pk_dev_… / pk_live_…）。secret key は絶対にゲームへ入れない */
  projectKey: string;
  /** Kumo API の URL。省略時は SDK を読み込んだ <script> の配信元 */
  apiUrl?: string;
  /** 起動時に自動でゲストログインするか（既定 true）。false なら auth.* を自分で呼ぶ */
  autoGuest?: boolean;
  /** 保存先（既定 localStorage）。テストや Electron で差し替える */
  storage?: KumoStorage;
  /** fetch の差し替え（テスト用） */
  fetch?: typeof fetch;
  /**
   * タブ間ロック（既定 navigator.locks = Web Locks API）。null = 使わない（409 refresh_race の再試行だけで凌ぐ）。
   * テストで「2 つのタブ」を再現するために差し替えられる
   */
  locks?: LockManagerLike | null;
  /** ネイティブアプリ（iOS / Android）での設定。Web では不要 */
  native?: KumoNativeOptions;
}

/**
 * Web Locks API（navigator.locks）のうち SDK が使う部分。options.signal = 待ちの上限（REFRESH_LOCK_WAIT_MS）。
 * signal が中断されると、鍵を取れていない要求は AbortError / TimeoutError で reject される（取れた後なら何もしない）
 */
export interface LockManagerLike {
  request(name: string, options: { signal?: AbortSignal }, callback: (lock: unknown) => Promise<unknown>): Promise<unknown>;
}

// KumoStorage は非同期（ネイティブの Keychain / Keystore）も受け付ける形に広げた（storage.ts の冒頭）。
// 同期の実装（localStorage など）はこれまで通りそのまま渡せる = 後方互換
export type { KumoStorage, SyncStorage } from './storage.js';

/**
 * ネイティブアプリ（Capacitor）での動作（2026-09-26 モバイル Phase 1）。Web では何も指定しなくてよい。
 * 省略時は window.Capacitor を自動検出する（native.ts）。
 */
export interface KumoNativeOptions {
  /** ネイティブの橋。undefined = 自動検出 / null = ネイティブ機能を使わない（Web と同じ動き）。テストで偽物を渡す */
  bridge?: NativeBridge | null;
  /**
   * トークンを Keychain / Keystore に置くか（既定 true）。KumoInitOptions.storage を明示したときはそちらが優先
   * （ゲームが自分で保存先を選んだなら尊重する）
   */
  secureStorage?: boolean;
  /**
   * 外部ログイン（ASWebAuthenticationSession / Custom Tabs）の戻り先。https のユニバーサルリンク / App Link
   * （例: https://<ゲームのドメイン>/.auth/callback）。config の auth.redirectUrls に入っている必要がある。
   * Kumo ホスティングは /.auth/* をアプリで開くパスとして AASA / assetlinks に載せる（2026-09-27 E2E-04 で中立なパスに。
   * 旧 /.kumo/auth/callback も当面受ける = すでに登録済みのアプリはそのまま動く）
   */
  authCallbackUrl?: string;
  /**
   * Android の Google ログインを Credential Manager（ネイティブのシート）で行うための Web クライアント ID（id_token の aud）。
   * 省略時は authCallbackUrl を使う Custom Tabs の経路
   */
  googleServerClientId?: string;
}

export interface KumoPlayer {
  id: string;
  /**
   * 表示名。ゲストのままなら null（ゲームは「Guest」などと出す）。サインインした（メール・KUMODeck・Google 等を持った）プレイヤーは
   * サーバーが必ず埋める: 入り方の名前、無ければ自動の名前「Player 7F3A」（2026-10-21 core/player-display-name・server の players/playerName.ts）
   */
  displayName: string | null;
  isGuest: boolean;
  createdAt: string;
  /** メール + パスワードのログインを確認済みか。メールログインが無ければ null（0013 identity） */
  emailVerified?: boolean | null;
  identities: Array<{ provider: string; email?: string; emailVerified?: boolean }>;
}

export interface KumoSession {
  player: KumoPlayer;
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
}

/**
 * KumoError の印（Symbol.for = 同じページのどのコピーからも同じ Symbol）。
 * WHY（2026-09-27 sdk-light）: 使うときだけ読む機能（/sdk/<機能>-<hash>.mjs・lazy.ts）は、それぞれ KumoError の
 * クラスのコピーを持つ（チャンク同士でコードを共有すると、核の /sdk.js の前にもう 1 ファイル読むことになり初回表示が遅れる）。
 * クラスが別物でも `e instanceof Kumo.KumoError` が今まで通り true になるよう、instanceof を印で判定する
 */
const KUMO_ERROR_BRAND = Symbol.for('kumo.KumoError');

/**
 * サーバーのエラー本文から写す「次の一手」（2026-09-29 creator/core-dx2 C1・docs/teams/core/core-dx2-findings.md）。
 * WHY: サーバーは `{ error: { code, message, details?, docs_url?, hint? } }` を返す（apps/server/src/lib/errors.ts・errorGuide.ts）。
 *   以前の SDK は code / message / details だけを写し、hint・docs_url が消えていた = ブラウザのコンソールで AI が読む一手が無かった
 */
export interface KumoErrorGuide {
  /** 何を直せば通るかの 1 文（英語）。サーバーの `error.hint`、または SDK が自分で投げるエラーの一部（save_too_large など） */
  hint?: string;
  /** 説明のページ（サーバーの `error.docs_url`。サーバーに DOCS_BASE_URL が無い環境・SDK だけのエラーでは無い） */
  docsUrl?: string;
}

export class KumoError extends Error {
  // declare = 値が無いときはプロパティ自体を作らない（undefined の独自プロパティが増えると assert.deepEqual・console の見た目が変わる）
  declare readonly hint?: string;
  declare readonly docsUrl?: string;
  /** 引数は足すだけ（status / code / message / details の順と意味は変えない = 既存の new KumoError(...) はそのまま動く） */
  constructor(public readonly status: number, public readonly code: string, message: string, public readonly details?: unknown, guide?: KumoErrorGuide) {
    super(message);
    this.name = 'KumoError';
    // 列挙しない = console・JSON・assert.deepEqual の見た目を変えない
    Object.defineProperty(this, KUMO_ERROR_BRAND, { value: true });
    // 空文字・文字列以外は付けない（壊れた本文の値を「読むべき一手」として出さない）
    if (typeof guide?.hint === 'string' && guide.hint) (this as { hint?: string }).hint = guide.hint;
    if (typeof guide?.docsUrl === 'string' && guide.docsUrl) (this as { docsUrl?: string }).docsUrl = guide.docsUrl;
  }
  static [Symbol.hasInstance](v: unknown): boolean {
    return typeof v === 'object' && v !== null && (v as Record<symbol, unknown>)[KUMO_ERROR_BRAND] === true;
  }
}

type Listener<T> = (value: T) => void;

export class Emitter<Events extends Record<string, unknown>> {
  private listeners: { [K in keyof Events]?: Array<Listener<Events[K]>> } = {};
  on<K extends keyof Events>(event: K, fn: Listener<Events[K]>): () => void {
    (this.listeners[event] ||= []).push(fn);
    return () => {
      const list = this.listeners[event];
      if (list) list.splice(list.indexOf(fn), 1);
    };
  }
  emit<K extends keyof Events>(event: K, value: Events[K]) {
    for (const fn of [...(this.listeners[event] || [])]) {
      // ゲーム側のハンドラの例外で SDK の処理を止めない
      try {
        fn(value);
      } catch (e) {
        // 接頭辞は中立な [game-backend]（2026-09-27 Quality PRIN-08: 段階 ① ではプレイヤーのコンソールにも Kumo の名前を出さない。SDK 内の console.* は全部この接頭辞）
        console.error('[game-backend] listener error', e);
      }
    }
  }
}

function memoryStorage(): SyncStorage {
  const m = new Map<string, string>();
  return { getItem: (k) => m.get(k) ?? null, setItem: (k, v) => void m.set(k, v), removeItem: (k) => void m.delete(k) };
}

function defaultStorage(): SyncStorage {
  try {
    const ls = globalThis.localStorage;
    const probe = '__kumo_probe__';
    ls.setItem(probe, '1');
    ls.removeItem(probe);
    return ls;
  } catch {
    // シークレットモード・サードパーティ iframe で localStorage が使えない → メモリ（リロードで別ゲストになる）
    return memoryStorage();
  }
}

function scriptOrigin(): string | undefined {
  try {
    const src = (globalThis.document?.currentScript as HTMLScriptElement | null)?.src;
    return src ? new URL(src).origin : undefined;
  } catch {
    return undefined;
  }
}
const LOADED_FROM = scriptOrigin();

/** 別タブが保存したアクセストークンを使う条件: 残りがこれ以上（期限直前のものを拾って即 401 → 再リフレッシュ、を避ける） */
const ACCESS_MIN_REMAINING_MS = 60_000;
/**
 * freshAccessToken が先にリフレッシュする残り時間（ms）。5 秒 = 端末からサーバーで認証されるまでの間に切れない余裕（1 往復 + DB 数往復）。
 * 大きくしない: 切れていないトークンを捨てると、そのぶんリフレッシュ（DB の数往復）が増える
 */
const WS_TOKEN_MARGIN_MS = 5_000;
/** refresh_race を受けたとき、別タブの書き込みを待つ上限と間隔（相手の応答の往復 + 保存。通常は数百 ms） */
const RACE_WAIT_MS = 3_000;
const RACE_POLL_MS = 100;
/**
 * タブ間の鍵（Web Locks）を待つ上限（2026-10-22 core/ios-signin-lock）。過ぎたら鍵なしでリフレッシュする。
 * WHY: iOS の Safari は裏のタブを止める（JS も通信も）。鍵を持ったまま止まったタブがあると、鍵は解放されない
 *   （タブが閉じられるまで）→ 新しく開いたタブの init（resume）が永遠に待ち、ゲームが「Loading…」のまま進まなかった
 *   （Kumo Studio ３の報告: iPhone で Quick Draw のタブが 4 つ以上残り、何度開き直しても進まない）。
 *   headless の WebKit で「鍵を持ったまま返さないタブ」を作ると、もう 1 つのタブの request は上限なしなら待ち続けることを確かめた。
 * 鍵なしで進めても安全な理由: 鍵が無い環境のための 409 refresh_race（サーバー）+ 読み直し・再試行（rotate）が既にある。
 *   止まったタブが後で目を覚まして古いトークンを出しても、猶予の中なら 409（系列は失効しない）。
 * 10 秒 = ふつうのリフレッシュ（1 往復・数百 ms）が別のタブで走っている待ちより十分長く、プレイヤーが諦めて閉じるより短い
 */
const REFRESH_LOCK_WAIT_MS = 10_000;

interface StoredAccess {
  token: string;
  /** 失効時刻（epoch ms・この端末の時計） */
  exp: number;
  player: KumoPlayer;
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function defaultLocks(): LockManagerLike | null {
  const locks = (globalThis as { navigator?: { locks?: LockManagerLike } }).navigator?.locks;
  return locks && typeof locks.request === 'function' ? locks : null;
}

/**
 * init で先読みする保存キーの接尾辞（`kumo:<pk の先頭>:<接尾辞>`）。非同期の保存領域（Keychain）から同期で読めるのは
 * 先読みしたキーだけなので、**SDK が core.storage に新しいキーを足したらここにも足す**
 *   refresh / access … core.ts のセッション、pending_purchase … store.ts のリダイレクト決済、oauth … authProviders.ts のリダイレクトログイン、
 *   share … share.ts の最初の流入元（共有 ID）と最後にプレイを記録した日、
 *   appeal … 復元で 401 になった最後のリフレッシュトークン（BAN された本人の異議の口 = sanctions.ts）
 */
export const STORAGE_SUFFIXES = ['refresh', 'access', 'pending_purchase', 'oauth', 'share', 'appeal'] as const;

export interface CoreEvents extends Record<string, unknown> {
  auth: KumoPlayer | null;
}

export class KumoCore extends Emitter<CoreEvents> {
  readonly apiUrl: string;
  readonly projectKey: string;
  /** SDK 内部が使う同期の面。非同期の保存領域（ネイティブ）は prepareStorage() で先読みしてから使う */
  readonly storage: CachedStorage;
  /** ネイティブアプリなら橋、Web なら null */
  readonly native: NativeBridge | null;
  readonly platform: KumoPlatform;
  readonly nativeOptions: KumoNativeOptions;
  private readonly fetchImpl: typeof fetch;
  private readonly locks: LockManagerLike | null;
  /** 鍵の待ちの上限（REFRESH_LOCK_WAIT_MS）。テストだけが短くする（signinIos.test.ts） */
  private lockWaitMs = REFRESH_LOCK_WAIT_MS;
  private accessToken: string | null = null;
  /**
   * accessToken の失効時刻（epoch ms・この端末の時計。2026-10-05 core/room-cold）。freshAccessToken が「切れているなら先に更新する」ために持つ。
   * WHY: 以前の freshAccessToken は手元のトークンを期限を見ずに返した。タブを開いたまま 15 分（アクセストークンの寿命）を過ぎてから
   * 部屋に入ると、WebSocket を張る → サーバーが認証で 4002 → リフレッシュ → もう 1 本張り直す、になり、
   * 待ちに「1 本目の接続 + 鍵の DB の 1 往復 + 4002 の往復」が余分に乗っていた（本番の 1 回目の connectMs 3.4 秒の候補）
   */
  private accessExp = 0;
  /** このタブが最後に使った / 受け取ったリフレッシュトークン。保存領域の値と違えば「別タブが回転した」 */
  private heldRefresh: string | null = null;
  private refreshing: Promise<void> | null = null;
  player: KumoPlayer | null = null;

  constructor(opts: KumoInitOptions) {
    super();
    if (!/^pk_(dev|live)_/.test(opts.projectKey || '')) {
      throw new KumoError(0, 'invalid_key', opts.projectKey?.startsWith('sk_')
        ? 'Never put a secret key (sk_…) in a game. Use the publishable key (pk_…).'
        : 'projectKey must be a publishable key (pk_dev_… or pk_live_…)');
    }
    const api = opts.apiUrl || LOADED_FROM;
    if (!api) throw new KumoError(0, 'missing_api_url', 'apiUrl is required when the SDK is bundled');
    this.apiUrl = api.replace(/\/+$/, '');
    this.projectKey = opts.projectKey;
    this.nativeOptions = opts.native ?? {};
    this.native = this.nativeOptions.bridge === undefined ? capacitorBridge() : this.nativeOptions.bridge;
    this.platform = this.native?.platform ?? 'web';
    // 優先順: ゲームが渡した storage > ネイティブの Keychain / Keystore > localStorage > メモリ
    const secure = !opts.storage && this.nativeOptions.secureStorage !== false ? nativeDefaultStorage(this.native) : null;
    this.storage = new CachedStorage(opts.storage || secure || defaultStorage());
    this.fetchImpl = opts.fetch || globalThis.fetch.bind(globalThis);
    this.locks = opts.locks === undefined ? defaultLocks() : opts.locks;
  }

  private get keyPrefix() {
    // 鍵そのものではなく先頭だけで区別（localStorage に鍵全文を増やさない）
    return this.projectKey.slice(0, 20);
  }

  /** SDK の保存キー（`kumo:<pk の先頭>:<suffix>`）。store.ts / authProviders.ts もこれで作る */
  storageKey(suffix: (typeof STORAGE_SUFFIXES)[number]) {
    return `kumo:${this.keyPrefix}:${suffix}`;
  }

  /**
   * 非同期の保存領域（Keychain / Keystore）を使うとき、SDK が使うキーを先読みする。init() が最初に呼ぶ。
   * 同期の保存領域（localStorage）なら何もしない。
   */
  async prepareStorage(): Promise<void> {
    await this.storage.preload(STORAGE_SUFFIXES.map((s) => this.storageKey(s)));
  }

  private get refreshKey() {
    return this.storageKey('refresh');
  }

  private get accessKey() {
    return this.storageKey('access');
  }

  get isSignedIn() {
    return !!this.accessToken;
  }

  /** ログイン系 API の応答を取り込む。 */
  adoptSession(s: KumoSession) {
    this.accessToken = s.accessToken;
    this.heldRefresh = s.refreshToken;
    this.storage.setItem(this.refreshKey, s.refreshToken);
    const access: StoredAccess = { token: s.accessToken, exp: Date.now() + s.expiresIn * 1000, player: s.player };
    this.accessExp = access.exp;
    this.storage.setItem(this.accessKey, JSON.stringify(access));
    this.player = s.player;
    this.emit('auth', s.player);
  }

  clearSession() {
    this.accessToken = null;
    this.heldRefresh = null;
    this.player = null;
    this.storage.removeItem(this.refreshKey);
    this.storage.removeItem(this.accessKey);
    this.emit('auth', null);
  }

  /**
   * プレイヤー情報だけが変わった（表示名の変更・メール連携）。保存領域のアクセス情報の player も書き換える:
   * 書き換えないと、再読み込みや別タブが保存領域から取り込んだときに古い表示名・isGuest が戻ってくる
   */
  updatePlayer(p: KumoPlayer) {
    this.player = p;
    const a = this.storedAccess();
    if (a && this.accessToken === a.token) this.storage.setItem(this.accessKey, JSON.stringify({ ...a, player: p }));
    this.emit('auth', p);
  }

  storedRefreshToken() {
    return this.storage.getItem(this.refreshKey);
  }

  /**
   * 危険な操作（連携・引き継ぎコード・ログイン手段の解除）に、この端末のリフレッシュトークンを「本人の端末である証明」として添えて送る
   * （サーバーの requireReauth・SEC-ACCESS-02。アクセストークンだけでは通らない）。
   * request() が途中でアクセストークンを更新する（= リフレッシュトークンを回転する）と、送った本文のトークンは古くなって
   * reauthentication_failed になる。保存領域の新しいトークンで 1 回だけやり直す（deleteAccount と同じ）
   */
  async withDeviceProof<T>(send: (proof: { refreshToken: string }) => Promise<T>): Promise<T> {
    const attempt = () => {
      const refreshToken = this.storedRefreshToken();
      if (!refreshToken) throw new KumoError(401, 'signed_out', 'Not signed in');
      return send({ refreshToken });
    };
    try {
      return await attempt();
    } catch (e) {
      if (!(e instanceof KumoError) || e.code !== 'reauthentication_failed') throw e;
      return attempt();
    }
  }

  /**
   * BAN の異議に使う「復元で 401 になったリフレッシュトークン」（sanctions.ts の appeal が POST /v1/auth/appeal に添える）。
   * WHY 残すか（2026-09-27 appeal-tools・Quality SEC-HIDE-03）: BAN するとサーバーは本人のリフレッシュトークンを全部失効させる
   *   → 次の起動の復元が 401 → ここで消すと、本人は二度と異議を出せない（サーバーの /v1/auth/appeal は
   *   「BAN と同時に失効したトークン」だけを本人の証明として受ける = integrity/routes.ts の APPEAL_REVOKE_WINDOW_MS）。
   * 401 の理由（BAN・期限切れ・盗難検知）はサーバーが区別して返さないので、どれでも残す。失効済みのトークンは
   *   復元にも危険な操作の証明（withDeviceProof は 'refresh' のキーを読む）にも使えない = 残しても渡せる力は異議だけ。
   * 上書きする（最後の 1 本だけ）: BAN の直後の起動で 401 になったものが一番新しい
   */
  appealToken(): string | null {
    return this.storage.getItem(this.storageKey('appeal'));
  }

  clearAppealToken() {
    this.storage.removeItem(this.storageKey('appeal'));
  }

  private storedAccess(): StoredAccess | null {
    try {
      const a = JSON.parse(this.storage.getItem(this.accessKey) || 'null') as StoredAccess | null;
      return a && typeof a.token === 'string' && typeof a.exp === 'number' && a.player ? a : null;
    } catch {
      return null;
    }
  }

  /**
   * 別のタブが回転済みなら、そのタブが保存したアクセストークンを取り込む（ネットワークに出ない）。
   * 取り込めたら true。storedRefresh = 今の保存値（heldRefresh と違うことを呼ぶ側が確かめてある）
   */
  private adoptFromStorage(storedRefresh: string): boolean {
    const a = this.storedAccess();
    if (!a || a.exp - Date.now() < ACCESS_MIN_REMAINING_MS) return false;
    // 今まさに 401 になったトークンと同じなら拾わない（拾うと「401 → 同じトークン → 401」になるだけ）
    if (a.token === this.accessToken) return false;
    const changedPlayer = this.player?.id !== a.player.id;
    this.accessToken = a.token;
    this.accessExp = a.exp;
    this.heldRefresh = storedRefresh;
    this.player = a.player;
    // 別タブでサインインし直した（別プレイヤーになった）場合だけ通知する（同じ人なら UI を揺らさない）
    if (changedPlayer) this.emit('auth', a.player);
    return true;
  }

  /**
   * タブ間ロック（Web Locks）の中で fn を実行。ロックが無ければそのまま。
   * REFRESH_LOCK_WAIT_MS 待っても取れなければ鍵なしで fn を実行する（定数の WHY）。fn 自身が投げたエラーはそのまま返す
   */
  private async withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
    if (!this.locks) return fn();
    // AbortSignal.timeout は使わない: 古い Safari（15 以前）に無いのと、取れた後に時計を止められない
    // （Node ではその時計がイベントループを生かさず、鍵の待ちだけが残るとテストが上限の前に終わる）
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(new DOMException('The sign-in lock wait timed out', 'TimeoutError')), this.lockWaitMs);
    let entered = false;
    try {
      return (await this.locks.request(`kumo-refresh:${this.keyPrefix}`, { signal: abort.signal }, () => {
        entered = true;
        clearTimeout(timer);
        return fn();
      })) as T;
    } catch (e) {
      // 鍵を取れて fn が投げた → そのまま。取れずに上限 / 中断 → 鍵なしで進む
      if (entered) throw e;
      console.warn('[game-backend] another tab held the sign-in lock too long; continuing without it');
      return fn();
    } finally {
      clearTimeout(timer);
    }
  }

  /** 保存済みリフレッシュトークンでセッションを復元。無い・失効なら false。 */
  async resume(): Promise<boolean> {
    return this.withRefreshLock(async () => {
      // ロックを取ってから読む: 待っている間に別タブが回転しているかもしれない
      const token = this.storedRefreshToken();
      if (!token) return false;
      if (token !== this.heldRefresh && this.adoptFromStorage(token)) return true;
      return this.rotate(token, false);
    });
  }

  /**
   * POST /v1/auth/refresh。401（失効・盗難検知・ログアウト済み）ならサインアウト状態にして false。
   * 409 refresh_race（別タブが直前に同じトークンを回転した。Web Locks が無い環境で起きる）なら、
   * 別タブの書き込みを待って読み直し、1 回だけ再試行する。書き換わらなければ refresh_race を投げる
   * （サインアウトはしない: 系列は生きているので、次の操作で読み直せば続けられる）
   */
  private async rotate(token: string, retried: boolean): Promise<boolean> {
    try {
      const s = await this.request<KumoSession>('POST', '/v1/auth/refresh', { refreshToken: token }, { auth: false });
      this.adoptSession(s);
      return true;
    } catch (e) {
      if (!(e instanceof KumoError)) throw e;
      if (e.status === 401) {
        // BAN で失効したトークンかもしれない = 異議の口のために残す（appealToken の WHY）
        this.storage.setItem(this.storageKey('appeal'), token);
        this.clearSession();
        return false;
      }
      if (e.code !== 'refresh_race' || retried) throw e;
      for (let waited = 0; waited <= RACE_WAIT_MS; waited += RACE_POLL_MS) {
        const now = this.storedRefreshToken();
        if (!now) {
          // 待っている間に別タブがサインアウトした
          this.clearSession();
          return false;
        }
        if (now !== token) {
          if (this.adoptFromStorage(now)) return true;
          return this.rotate(now, true);
        }
        await sleep(RACE_POLL_MS);
      }
      throw e;
    }
  }

  private async refreshOnce() {
    if (!this.refreshing) {
      this.refreshing = (async () => {
        const ok = await this.resume();
        if (!ok) throw new KumoError(401, 'signed_out', 'Session ended; sign in again');
      })().finally(() => {
        this.refreshing = null;
      });
    }
    return this.refreshing;
  }

  /**
   * API 呼び出し。auth=true（既定）ならアクセストークンを付け、期限切れなら 1 回だけリフレッシュして再試行。
   */
  async request<T>(method: string, path: string, body?: unknown, { auth = true, idempotencyKey, keepalive }: { auth?: boolean; idempotencyKey?: string; keepalive?: boolean } = {}): Promise<T> {
    const doFetch = async () => {
      const headers: Record<string, string> = { 'X-Kumo-Key': this.projectKey };
      if (body !== undefined) headers['Content-Type'] = 'application/json';
      if (auth) {
        if (!this.accessToken) throw new KumoError(401, 'signed_out', 'Not signed in. Call kumo.auth.guest() or enable autoGuest.');
        headers.Authorization = `Bearer ${this.accessToken}`;
      }
      if (idempotencyKey) headers['Idempotency-Key'] = idempotencyKey;
      return this.fetchImpl(this.apiUrl + path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), keepalive });
    };

    let res = await doFetch();
    if (res.status === 401 && auth) {
      const err = await res.clone().json().catch(() => null);
      if (err?.error?.code === 'token_expired') {
        await this.refreshOnce();
        res = await doFetch();
      }
    }
    const json = await res.json().catch(() => null);
    if (!res.ok) {
      const e = json?.error;
      // hint / docs_url はサーバーの本文の名前（docs_url は snake_case）→ SDK では hint / docsUrl（C1）
      throw new KumoError(res.status, e?.code || `http_${res.status}`, e?.message || `HTTP ${res.status}`, e?.details, { hint: e?.hint, docsUrl: e?.docs_url });
    }
    return json as T;
  }

  /**
   * WebSocket 用: 有効なアクセストークンを返す。無い・切れている（残り WS_TOKEN_MARGIN_MS 未満）なら先にリフレッシュする
   * （accessExp の WHY）。残りがわずかでも切れていなければそのまま使う: WS の認証は接続した時の 1 回だけで、つながった後は期限で切られない
   */
  async freshAccessToken(): Promise<string> {
    if (!this.accessToken || this.accessExp - Date.now() < WS_TOKEN_MARGIN_MS) await this.refreshOnce();
    return this.accessToken!;
  }

  async forceRefresh(): Promise<string> {
    await this.refreshOnce();
    return this.accessToken!;
  }
}
